import { httpsCallable, type HttpsCallableResult } from 'firebase/functions';
import { functions } from './firebase';
import type { FeedResponse } from '@/domain/apiResponses/feed/feedResponse';
import type { UserMediaLikeDto } from '@/data/dtos/userMediaLike/userMediaLikeDto';
import type { NotificationInfoDto } from '@/data/dtos/info/infoDtos';
import type { UserReportDto } from '@/data/dtos/report/userReportDto';
import type { ContentReportDto } from '@/data/dtos/report/contentReportDto';
import type { AppNotificationDto } from '@/data/dtos/notification/appNotificationDto';
import type { UserReportAppealDto } from '@/data/dtos/report/userReportAppealDto';
import type { ContentReportAppealDto } from '@/data/dtos/report/contentReportAppealDto';
import type { AppTransactionDto } from '@/data/dtos/transaction/appTransactionDto';
import type { RoadmapItemLikeRequestDto } from '@/data/dtos/roadmapItemLike/roadmapItemLikeRequestDto';
import type { OnboardInfoDto } from '@/data/dtos/user/onboardInfoDto';
import type { RoadmapItemDto } from '@/data/dtos/roadmapItem/roadmapItemDto';
import type { ExchangeRateDto } from '@/data/dtos/payment/exchangeRateDto';
import type { ChatMediaCdnTokensDto } from '@/data/dtos/encryption/chatMediaCdnTokensDto';
import type { CommunityMediaCdnTokensDto } from '@/data/dtos/encryption/communityMediaCdnTokensDto';
import type { JsonObject } from '@/data/types/json';
import { appLogger } from '@/domain/services/analytics/appLogger';

/**
 * CloudFunctionsService
 * 
 * Service layer for all Firebase Cloud Functions calls.
 * Matches the Flutter CloudFunctionsService architecture for consistency.
 * All UI components should use this service instead of direct Firestore calls.
 */
export class CloudFunctionsService {
  private static instance: CloudFunctionsService;

  public static getInstance(): CloudFunctionsService {
    if (!CloudFunctionsService.instance) {
      CloudFunctionsService.instance = new CloudFunctionsService();
    }
    return CloudFunctionsService.instance;
  }

  /**
   * Helper method to call a cloud function
   */
  private async callFunction<T>(
    functionName: string,
    data?: JsonObject
  ): Promise<T> {
    try {
      const callable = httpsCallable(functions, functionName);
      const result: HttpsCallableResult = await callable(data || {});
      return result.data as T;
    } catch (error) {
      appLogger.error(`Error calling ${functionName}`, 'CloudFunctionsService.callFunction', { error: error as Error });
      throw error;
    }
  }

  /**
   * Add a new user (matching Flutter addUser)
   */
  // TODO(json-pattern): replace JsonObject with a named CreateUserDto once the user-creation payload is normalized
  async addUser(userData: JsonObject, privateJwk: JsonObject): Promise<boolean> {
    try {
      const response = await this.callFunction<{ success: boolean }>('addUser', {
        user: userData,
        privateJwk: privateJwk
      });
      return response.success ?? false;
    } catch (error) {
      appLogger.error('Error addUser', 'CloudFunctionsService.addUser', { error: error as Error });
      return false;
    }
  }

  /**
   * Set user encryption keys (matching Flutter setUserEncryptionKeys)
   */
  // TODO(json-pattern): replace JsonObject with a named JwkDto once the JWK payload shape is normalized
  async setUserEncryptionKeys(
    publicJwk: JsonObject,
    privateJwk: JsonObject
  ): Promise<boolean> {
    try {
      const response = await this.callFunction<{ success: boolean }>('setUserEncryptionKeys', {
        publicJwk,
        privateJwk
      });
      return response.success ?? false;
    } catch (error) {
      appLogger.error('Error setUserEncryptionKeys', 'CloudFunctionsService.setUserEncryptionKeys', { error: error as Error });
      return false;
    }
  }

  /**
   * Get user private JWK (matching Flutter getUserPrivateJwk).
   *
   * `HttpsCallableResult.data` equals Flutter's `response.data` (both are the
   * cloud function's raw return value); the cloud function returns
   * `{ data: { privateJwk: ... } }`, so on web we read `response.data.privateJwk`
   * — same number of unwrap steps as Flutter's
   * `response.data['data']['privateJwk']` (lib/services/cloud_functions_service.dart:566).
   */
  // TODO(json-pattern): replace JsonObject with a named JwkDto once the JWK payload shape is normalized — the returned JWK is the inner key material, not the outer wrapper
  async getUserPrivateJwk(): Promise<JsonObject | null> {
    try {
      const response = await this.callFunction<{ data: { privateJwk: JsonObject } }>('getUserPrivateJwk');
      return response?.data?.privateJwk ?? null;
    } catch (error) {
      appLogger.error('Error getUserPrivateJwk', 'CloudFunctionsService.getUserPrivateJwk', { error: error as Error });
      return null;
    }
  }

  /**
   * Toggle a user setting (matching Flutter toggleUserSetting)
   * @param settingName - Name of the setting to toggle
   * @returns Success status
   */
  async toggleUserSetting(settingName: string): Promise<boolean> {
    try {
      const response = await this.callFunction<{ data: { success: boolean } }>('toggleUserSetting', {
        settingName
      });
      return response.data?.success ?? false;
    } catch (error) {
      appLogger.error('Error toggleUserSetting', 'CloudFunctionsService.toggleUserSetting', { error: error as Error });
      return false;
    }
  }

  /**
   * Fetch user feed (matching Flutter fetchFeed)
   * Returns raw data - remapping is handled by domain layer UseCase
   */
  async fetchFeed(): Promise<FeedResponse> {
    try {
      const response = await this.callFunction<{ response: FeedResponse }>('fetchFeed');

      // Return raw response - UseCase will handle remapping
      return response.response;
    } catch (error) {
      appLogger.error('Error fetchFeed', 'CloudFunctionsService.fetchFeed', { error: error as Error });
      return {
        medias: [],
        hasMore: false
      };
    }
  }

  /**
   * Toggle User Media Like (matching Flutter ContentReactionsDataSource.likeContent)
   * 
   * @param like - User media like DTO
   * @param notification - Notification info DTO
   * @returns Success status
   */
  async toggleUserMediaLike(
    like: UserMediaLikeDto,
    notification: NotificationInfoDto
  ): Promise<boolean> {
    try {
      // returns ToggleUserMediaLikeResponse object on the backend
      const response = await this.callFunction<{ success: boolean }>('toggleUserMediaLike', {
        userMediaLike: like,
        notification: notification
      });

      return response.success ?? false;
    } catch (error) {
      appLogger.error('Error toggleUserMediaLike', 'CloudFunctionsService.toggleUserMediaLike', { error: error as Error });
      return false;
    }
  }

  /**
   * Delete account data (matching Flutter deleteAccountData)
   */
  async deleteAccountData(): Promise<void> {
    try {
      await this.callFunction<void>('deleteAccountData');
    } catch (error) {
      appLogger.error('Error deleteAccountData', 'CloudFunctionsService.deleteAccountData', { error: error as Error });
      throw error;
    }
  }

  /**
   * Subscribe/unsubscribe to a user (matching Flutter subscribeToUser).
   *
   * Cloud Function returns a raw boolean (Promise<boolean>), not a wrapper
   * object — see cloud_functions/functions/src/functions/user_subscriptions/subscribe_to_user.ts.
   */
  async subscribeToUser(userId: string, subscribe: boolean): Promise<boolean> {
    try {
      const response = await this.callFunction<boolean>('subscribeToUser', { userId, subscribe });
      return response ?? false;
    } catch (error) {
      appLogger.error('Error subscribeToUser', 'CloudFunctionsService.subscribeToUser', { error: error as Error });
      return false;
    }
  }

  /**
   * Request/cancel exclusive subscription for an approval-required creator
   * (matching Flutter requestExclusiveSubscription).
   *
   * Cloud Function returns a raw boolean (Promise<boolean>), not a wrapper
   * object — see cloud_functions/functions/src/functions/user_subscriptions/request_exclusive_subscription.ts.
   */
  async requestExclusiveSubscription(userId: string, requested: boolean): Promise<boolean> {
    try {
      const response = await this.callFunction<boolean>('requestExclusiveSubscription', { userId, requested });
      return response ?? false;
    } catch (error) {
      appLogger.error('Error requestExclusiveSubscription', 'CloudFunctionsService.requestExclusiveSubscription', { error: error as Error });
      return false;
    }
  }

  /**
   * Review (accept/reject) an exclusive subscription request as the creator
   * (matching Flutter reviewExclusiveSubscription —
   * lib/services/cloud_functions_service.dart:462-483).
   *
   * Cloud Function returns a raw boolean (Promise<boolean>), not a wrapper
   * object — see cloud_functions/functions/src/functions/user_subscriptions/review_exclusive_subscription.ts:118.
   *
   * A `false` return is NOT necessarily an error: the Cloud Function resolves
   * `false` when the request's subscription doc no longer exists, i.e. the
   * request was cancelled before the reviewer answered (review_exclusive_subscription.ts:26-29).
   *
   * The reviewer identity is taken server-side from `auth.uid`
   * (review_exclusive_subscription.ts:18) behind an `unauthenticated` gate
   * (:10-15), so no client-side gate is added here. The Cloud Function owns the
   * whole side-effect envelope (outcome notifications, `actioned: true` on the
   * original request, subscription deletes on reject, `handleUserSubscription`
   * on accept) — this client reimplements none of it.
   *
   * @param requestNotificationId - Id of the original request notification
   * @param userId - Id of the requesting (peer) user
   * @param accept - True to confirm the request, false to reject it
   * @returns Success status
   */
  async reviewExclusiveSubscription(
    requestNotificationId: string,
    userId: string,
    accept: boolean
  ): Promise<boolean> {
    try {
      const response = await this.callFunction<boolean>('reviewExclusiveSubscription', {
        userId,
        requestNotificationId,
        accept
      });
      return response ?? false;
    } catch (error) {
      appLogger.error('Error reviewExclusiveSubscription', 'CloudFunctionsService.reviewExclusiveSubscription', { error: error as Error });
      return false;
    }
  }

  /**
   * Block a user (matching Flutter blockUser)
   */
  async blockUser(userId: string, groupChatId: string): Promise<boolean> {
    try {
      const response = await this.callFunction<{ success: boolean }>('blockUser', {
        userId,
        groupChatId
      });
      return response.success ?? false;
    } catch (error) {
      appLogger.error('Error blockUser', 'CloudFunctionsService.blockUser', { error: error as Error });
      return false;
    }
  }

  /**
   * Delete content (matching Flutter deleteContentStorage)
   */
  async deleteContentStorage(contentId: string): Promise<boolean> {
    try {
      const response = await this.callFunction<{ success: boolean }>('deleteContentStorage', {
        contentId,
      });
      return response.success ?? false;
    } catch (error) {
      appLogger.error('Error deleteContentStorage', 'CloudFunctionsService.deleteContentStorage', { error: error as Error });
      return false;
    }
  }

  /**
   * Force-publish content — used to publish a draft or scheduled item now
   * (matching Flutter forcePublishContent).
   *
   * Cloud Function returns a raw boolean (Promise<boolean>), not a wrapper
   * object — see cloud_functions/functions/src/functions/content/force_publish_content.ts.
   */
  async forcePublishContent(contentId: string): Promise<boolean> {
    try {
      const response = await this.callFunction<boolean>('forcePublishContent', { contentId });
      return response ?? false;
    } catch (error) {
      appLogger.error('Error forcePublishContent', 'CloudFunctionsService.forcePublishContent', { error: error as Error });
      return false;
    }
  }

  /**
   * Hide content (matching Flutter hideContent)
   */
  async hideContent(contentId: string): Promise<boolean> {
    try {
      const response = await this.callFunction<{ success: boolean }>('hideContent', {
        contentId
      });
      return response.success ?? false;
    } catch (error) {
      appLogger.error('Error hideContent', 'CloudFunctionsService.hideContent', { error: error as Error });
      return false;
    }
  }

  /**
   * Retry the premium-video payment for a content whose upload payment failed
   * (matching Flutter `cloud_functions_service.dart:1171-1184`).
   *
   * Callable: `retryPremiumVideoPayment` (verbatim from
   * `cloud_functions_service.dart:1175`). Payload: `{ contentId }`.
   *
   * The Cloud Function wraps its result in a `{ data: { success } }` envelope
   * (`cloud_functions/functions/src/functions/payment/retry_premium_video_payment.ts:20-24`).
   * The Firebase SDK already unwraps one `result.data` level in `callFunction`,
   * so the boolean lives at `response.data.success` — reading `response.success`
   * would be `undefined` and always yield `false`.
   *
   * Auth: the Cloud Function throws `HttpsError('unauthenticated', …)` when there
   * is no authenticated caller (`retry_premium_video_payment.ts:8-13`); the gate is
   * server-side, so no additional client-side gate is applied here.
   *
   * Verified Flutter bug, since fixed in both clients: Dart used to set
   * `success = true` right after `callable.call` and never inspect the result, so
   * a server-side failure read as success there. The Dart wrapper now reads the
   * same `data.data.success` path this one does, so the two clients agree — do
   * not "restore parity" by dropping the result check on either side.
   */
  async retryPremiumVideoPayment(contentId: string): Promise<boolean> {
    try {
      const response = await this.callFunction<{ data?: { success?: boolean } }>(
        'retryPremiumVideoPayment',
        { contentId }
      );
      return response.data?.success ?? false;
    } catch (error) {
      appLogger.error('Error retryPremiumVideoPayment', 'CloudFunctionsService.retryPremiumVideoPayment', { error: error as Error });
      return false;
    }
  }

  /**
   * Retry the boost-video payment for a content whose boost payment failed
   * (matching Flutter `cloud_functions_service.dart:1186-1199`).
   *
   * Callable: `retryBoostVideoPayment` (verbatim from
   * `cloud_functions_service.dart:1190`). Payload: `{ contentId }`.
   *
   * The Cloud Function wraps its result in a `{ data: { success } }` envelope
   * (`cloud_functions/functions/src/functions/payment/retry_boost_video_payment.ts:20-24`).
   * The Firebase SDK already unwraps one `result.data` level in `callFunction`,
   * so the boolean lives at `response.data.success` — reading `response.success`
   * would be `undefined` and always yield `false`.
   *
   * Auth: the Cloud Function throws `HttpsError('unauthenticated', …)` when there
   * is no authenticated caller (`retry_boost_video_payment.ts:8-13`); the gate is
   * server-side, so no additional client-side gate is applied here.
   *
   * Verified Flutter bug, since fixed in both clients: Dart used to set
   * `success = true` right after `callable.call` and never inspect the result, so
   * a server-side failure read as success there. The Dart wrapper now reads the
   * same `data.data.success` path this one does, so the two clients agree — do
   * not "restore parity" by dropping the result check on either side.
   */
  async retryBoostVideoPayment(contentId: string): Promise<boolean> {
    try {
      const response = await this.callFunction<{ data?: { success?: boolean } }>(
        'retryBoostVideoPayment',
        { contentId }
      );
      return response.data?.success ?? false;
    } catch (error) {
      appLogger.error('Error retryBoostVideoPayment', 'CloudFunctionsService.retryBoostVideoPayment', { error: error as Error });
      return false;
    }
  }

  /**
   * Trigger last seen timestamp update (matching Flutter triggerLastSeenTimestamp)
   */
  async triggerLastSeenTimestamp(platform: number): Promise<void> {
    try {
      await this.callFunction<void>('setLastSeenTimestamp', { platform });
    } catch (error) {
      appLogger.error('Error triggerLastSeenTimestamp', 'CloudFunctionsService.triggerLastSeenTimestamp', { error: error as Error });
    }
  }

  /**
   * Process feed tags watch (matching Flutter processFeedTags).
   *
   * Fire-and-forget: errors are logged and swallowed, mirroring Flutter's
   * `cloud_functions_service.dart:971-981`.
   */
  async processFeedTags(contentId: string): Promise<void> {
    try {
      await this.callFunction<void>('processFeedTags', { contentId });
    } catch (error) {
      appLogger.error('Error processFeedTags', 'CloudFunctionsService.processFeedTags', { error: error as Error });
    }
  }

  /**
   * Increment content views (matching Flutter incrementContentViews).
   * Source: lib/services/cloud_functions_service.dart:1048-1067.
   */
  async incrementContentViews(userId: string, contentId: string): Promise<boolean> {
    try {
      const response = await this.callFunction<{ success: boolean }>('incrementContentViews', {
        userId,
        contentId,
      });
      return response.success ?? false;
    } catch (error) {
      appLogger.error('Error incrementContentViews', 'CloudFunctionsService.incrementContentViews', { error: error as Error });
      return false;
    }
  }

  /**
   * Save the current playback position to the user's watch history
   * (matching Flutter saveToWatchHistory).
   * Source: lib/services/cloud_functions_service.dart:1069-1090.
   */
  async saveToWatchHistory(
    contentId: string,
    currentMillis: number,
    totalMillis: number,
  ): Promise<boolean> {
    try {
      const response = await this.callFunction<{ success: boolean }>('saveToWatchHistory', {
        contentId,
        currentMillis,
        totalMillis,
      });
      return response.success ?? false;
    } catch (error) {
      appLogger.error('Error saveToWatchHistory', 'CloudFunctionsService.saveToWatchHistory', { error: error as Error });
      return false;
    }
  }

  /**
   * Onboard / update user profile (matching Flutter onboardUser cloud function)
   * Despite the name, this function supports both initial onboarding and profile updates.
   */
  async onboardUser(onboardInfo: OnboardInfoDto): Promise<boolean> {
    try {
      const response = await this.callFunction<{ data: { success: boolean } }>('onboardUser', {
        onboardInfo
      });
      return response.data?.success ?? false;
    } catch (error) {
      appLogger.error('Error onboardUser', 'CloudFunctionsService.onboardUser', { error: error as Error });
      return false;
    }
  }

  /**
   * Add a new roadmap item (matching Flutter CloudFunctionsService.addRoadmapItem,
   * lib/services/cloud_functions_service.dart:995-1010).
   *
   * The cloud function returns `{ data: { success: boolean } }`; on web
   * `HttpsCallableResult.data` equals Flutter's `response.data`, so reading
   * `response.data.success` mirrors Flutter's `response.data['data']['success']`.
   */
  async addRoadmapItem(item: RoadmapItemDto): Promise<boolean> {
    try {
      const response = await this.callFunction<{ data: { success: boolean } }>('addRoadmapItem', {
        item
      });
      return response.data?.success ?? false;
    } catch (error) {
      appLogger.error('Error addRoadmapItem', 'CloudFunctionsService.addRoadmapItem', { error: error as Error });
      return false;
    }
  }

  /**
   * Report a user (matching Flutter ReportService.reportUser)
   * Cloud function: reportUser
   *
   * @param userReport - The report data (mirrors Flutter's UserReport.toJson())
   * @param notification - The notification payload (mirrors Flutter's AppNotification.toJson())
   */
  async reportUser(
    userReport: UserReportDto,
    notification: AppNotificationDto
  ): Promise<void> {
    try {
      await this.callFunction<void>('reportUser', {
        userReport,
        notification,
      });
    } catch (error) {
      appLogger.error('Error reportUser', 'CloudFunctionsService.reportUser', { error: error as Error });
      throw error;
    }
  }

  /**
   * Report content (matching Flutter ReportService.reportContent)
   * Cloud function: reportContent
   *
   * @param contentReport - The report data (mirrors Flutter's ContentReport.toJson())
   * @param notification - The notification payload (mirrors Flutter's AppNotification.toJson())
   */
  async reportContent(
    contentReport: ContentReportDto,
    notification: AppNotificationDto
  ): Promise<void> {
    try {
      await this.callFunction<void>('reportContent', {
        contentReport,
        notification,
      });
    } catch (error) {
      appLogger.error('Error reportContent', 'CloudFunctionsService.reportContent', { error: error as Error });
      throw error;
    }
  }

  /**
   * Appeal a user report (matching Flutter ReportService.appealReportedUser)
   * Cloud function: appealReportedUser
   *
   * @param appeal - The appeal data (mirrors Flutter's UserReportAppeal.toJson())
   */
  async appealReportedUser(appeal: UserReportAppealDto): Promise<void> {
    try {
      await this.callFunction<void>('appealReportedUser', { appeal });
    } catch (error) {
      appLogger.error('Error appealReportedUser', 'CloudFunctionsService.appealReportedUser', { error: error as Error });
      throw error;
    }
  }

  /**
   * Appeal a content report (matching Flutter ReportService.appealReportedContent)
   * Cloud function: appealReportedContent
   */
  async appealReportedContent(appeal: ContentReportAppealDto): Promise<void> {
    try {
      await this.callFunction<void>('appealReportedContent', { appeal });
    } catch (error) {
      appLogger.error('Error appealReportedContent', 'CloudFunctionsService.appealReportedContent', { error: error as Error });
      throw error;
    }
  }

  /**
   * Generate Media CDN tokens for encrypted video playback
   * Returns HMAC token for CDN authentication and encrypted decryption keys
   * 
   * @param videoId - The ID of the video to generate tokens for
   * @returns Object containing:
   *   - hdnts: HMAC token for CDN access
   *   - dk: Encrypted video decryption key (with ciphertext and iv)
   *   - iv: Encrypted IV key (with ciphertext and iv)
   */
  async generateMediaCdnTokens(videoId: string): Promise<{
    hdnts: string;
    dk: { ciphertext: Uint8Array; iv: Uint8Array };
    iv: { ciphertext: Uint8Array; iv: Uint8Array };
  } | null> {
    try {
      const response = await this.callFunction<{
        hdnts: string;
        dk: { ciphertext: unknown; iv: unknown };
        iv: { ciphertext: unknown; iv: unknown };
      }>('generateMediaCdnTokens', { videoId: videoId });

      return this.normalizeMediaCdnTokensResponse(response);
    } catch (error) {
      appLogger.error('Error generateMediaCdnTokens', 'CloudFunctionsService.generateMediaCdnTokens', { error: error as Error });
      return null;
    }
  }

  /**
   * Generate chat-media CDN tokens for encrypted chat-media playback.
   *
   * Chat-scoped mirror of {@link generateMediaCdnTokens}. Calls the
   * `generateChatMediaCdnTokens` callable, which returns a byte-identical
   * response shape (`hdnts` + encrypted `dk`/`iv`). The callable authorizes the
   * verified caller against the two chat participants and the subscription
   * predicate server-side; the client only forwards the identifiers.
   *
   * @param documentId - The chat message document id keying the secrets/path.
   * @param peerId - The chat counterparty user id (subscription counterparty).
   * @returns The decoded {@link ChatMediaCdnTokensDto}, or `null` on error.
   */
  async generateChatMediaCdnTokens(
    documentId: string,
    peerId: string,
  ): Promise<ChatMediaCdnTokensDto | null> {
    try {
      const response = await this.callFunction<{
        hdnts: string;
        dk: { ciphertext: unknown; iv: unknown };
        iv: { ciphertext: unknown; iv: unknown };
      }>('generateChatMediaCdnTokens', { documentId, peerId });

      return this.normalizeMediaCdnTokensResponse(response);
    } catch (error) {
      appLogger.error('Error generateChatMediaCdnTokens', 'CloudFunctionsService.generateChatMediaCdnTokens', { error: error as Error });
      return null;
    }
  }

  /**
   * Generate community-media CDN tokens for encrypted community-video playback.
   *
   * Community-scoped mirror of {@link generateChatMediaCdnTokens}. Calls the
   * `generateCommunityMediaCdnTokens` callable, which returns a byte-identical
   * response shape (`hdnts` + encrypted `dk`/`iv`). The callable authorizes the
   * verified caller server-side against the community access rule (the wall
   * owner always has access; every other viewer needs an active exclusive-content
   * subscription to the owner); the client only forwards the identifiers.
   *
   * @param messageId - The community message id keying the secrets/path.
   * @param communityOwner - The community wall owner id (access-gate counterparty).
   * @returns The decoded {@link CommunityMediaCdnTokensDto}, or `null` on error.
   */
  async generateCommunityMediaCdnTokens(
    messageId: string,
    communityOwner: string,
  ): Promise<CommunityMediaCdnTokensDto | null> {
    try {
      const response = await this.callFunction<{
        hdnts: string;
        dk: { ciphertext: unknown; iv: unknown };
        iv: { ciphertext: unknown; iv: unknown };
      }>('generateCommunityMediaCdnTokens', { messageId, communityOwner });

      return this.normalizeMediaCdnTokensResponse(response);
    } catch (error) {
      appLogger.error('Error generateCommunityMediaCdnTokens', 'CloudFunctionsService.generateCommunityMediaCdnTokens', { error: error as Error });
      return null;
    }
  }

  /**
   * Normalize a Media CDN tokens callable response into typed `Uint8Array`s.
   *
   * Firebase serializes Node.js Buffers to objects with numeric keys
   * (`{0: val, 1: val, ...}`); this converts those (and plain arrays) back to
   * `Uint8Array`. Shared by {@link generateMediaCdnTokens},
   * {@link generateChatMediaCdnTokens} and {@link generateCommunityMediaCdnTokens}
   * so the proxies cannot drift.
   */
  private normalizeMediaCdnTokensResponse(response: {
    hdnts: string;
    dk: { ciphertext: unknown; iv: unknown };
    iv: { ciphertext: unknown; iv: unknown };
  }): {
    hdnts: string;
    dk: { ciphertext: Uint8Array; iv: Uint8Array };
    iv: { ciphertext: Uint8Array; iv: Uint8Array };
  } {
    const toUint8Array = (obj: unknown): Uint8Array => {
      if (obj instanceof Uint8Array) return obj;
      if (Array.isArray(obj)) return new Uint8Array(obj);
      // Object with numeric keys - convert to array
      const numericObj = obj as Record<string | number, number>;
      const keys = Object.keys(numericObj).map(Number).sort((a, b) => a - b);
      const arr = keys.map(k => numericObj[k]);
      return new Uint8Array(arr);
    };

    return {
      hdnts: response.hdnts,
      dk: {
        ciphertext: toUint8Array(response.dk.ciphertext),
        iv: toUint8Array(response.dk.iv),
      },
      iv: {
        ciphertext: toUint8Array(response.iv.ciphertext),
        iv: toUint8Array(response.iv.iv),
      },
    };
  }

  /**
   * Transcode a user video after upload (matching Flutter CloudFunctionsService.transcodeUserContent).
   * Invokes the `transcodeUserVideo` callable. Image uploads are out of scope for web.
   */
  async transcodeUserContent(payload: JsonObject): Promise<boolean> {
    try {
      const response = await this.callFunction<{ success?: boolean }>(
        'transcodeUserVideo',
        payload,
      );
      return response?.success ?? false;
    } catch (error) {
      appLogger.error('Error transcodeUserContent', 'CloudFunctionsService.transcodeUserContent', { error: error as Error });
      return false;
    }
  }

  /**
   * Transcode a chat-media file after upload (matching Flutter
   * CloudFunctionsService.transcodeChatContent at
   * lib/services/cloud_functions_service.dart:157-190).
   *
   * Dispatches to `transcodeChatImage` when `mediaType === 0`, otherwise
   * `transcodeChatVideo` — mirroring Flutter's
   * `media.type == 0 ? 'transcodeChatImage' : 'transcodeChatVideo'`
   * (cloud_functions_service.dart:168-169).
   *
   * Fire-and-forget: Flutter fires the callable without awaiting and swallows
   * any error in a try/catch (cloud_functions_service.dart:187-189). We
   * replicate that — log and do NOT throw — so the upload result is unaffected.
   *
   * The payload is assembled by the domain caller as a plain `JsonObject`
   * (keeping the data-layer surface JSON-only per data-layer.md).
   */
  async transcodeChatContent(payload: JsonObject, mediaType: number): Promise<void> {
    const functionName = mediaType === 0 ? 'transcodeChatImage' : 'transcodeChatVideo';
    try {
      await this.callFunction<void>(functionName, payload);
    } catch (error) {
      appLogger.error('Error transcodeChatContent', 'CloudFunctionsService.transcodeChatContent', { error: error as Error });
    }
  }

  /**
   * Transcode a community-message media file after upload (matching Flutter
   * CloudFunctionsService.transcodeCommunityMessageContent at
   * lib/services/cloud_functions_service.dart:192-228).
   *
   * Dispatches to `transcodeCommunityMessageImage` when `mediaType === 0`,
   * otherwise `transcodeCommunityMessageVideo` — mirroring Flutter's
   * `media.type == 0 ? 'transcodeCommunityMessageImage' : 'transcodeCommunityMessageVideo'`
   * (cloud_functions_service.dart:203-206). Never invent the name.
   *
   * Fire-and-forget: Flutter fires the callable without awaiting and swallows
   * any error in a try/catch (cloud_functions_service.dart:225-227). We
   * replicate that — log and do NOT throw — so the upload result is unaffected.
   *
   * The payload (`filePath`, `description`, `duration`, `fileExtension`,
   * `aspectRatio`, `timeStartExpression`, `timeDurationExpression`,
   * `resizeWidth`, `resizeHeight`, `messageId` — cloud_functions_service.dart:213-224)
   * is assembled by the domain caller as a plain `JsonObject` (keeping the
   * data-layer surface JSON-only per data-layer.md).
   */
  async transcodeCommunityMessageContent(payload: JsonObject, mediaType: number): Promise<void> {
    const functionName =
      mediaType === 0 ? 'transcodeCommunityMessageImage' : 'transcodeCommunityMessageVideo';
    try {
      await this.callFunction<void>(functionName, payload);
    } catch (error) {
      appLogger.error(
        'Error transcodeCommunityMessageContent',
        'CloudFunctionsService.transcodeCommunityMessageContent',
        { error: error as Error },
      );
    }
  }

  /**
   * Send a gift / unlock content transaction (matching Flutter CloudFunctionsService.sendGift)
   */
  async sendGift(
    appTransaction: AppTransactionDto,
    notification: AppNotificationDto | null,
    groupChatId: string | null,
  ): Promise<boolean> {
    try {
      const response = await this.callFunction<{ data: { success: boolean } }>('sendGift', {
        appTransaction,
        notification,
        groupChatId,
      });
      return response.data?.success ?? false;
    } catch (error) {
      appLogger.error('Error sendGift', 'CloudFunctionsService.sendGift', { error: error as Error });
      return false;
    }
  }

  /**
   * Persist a roadmap item like/dislike (matching Flutter
   * CloudFunctionsService.saveRoadmapItemLike at
   * lib/services/cloud_functions_service.dart:1012-1029).
   *
   * Callable name `'saveRoadmapItemLike'`, payload `{ likeRequest }`, success
   * read from the `{ data: { success } }` envelope with a `false` default on
   * any error.
   */
  async saveRoadmapItemLike(likeRequest: RoadmapItemLikeRequestDto): Promise<boolean> {
    try {
      const response = await this.callFunction<{ data: { success: boolean } }>('saveRoadmapItemLike', {
        likeRequest,
      });
      return response.data?.success ?? false;
    } catch (error) {
      appLogger.error('Error saveRoadmapItemLike', 'CloudFunctionsService.saveRoadmapItemLike', { error: error as Error });
      return false;
    }
  }

  /**
   * Submit a support request (matching Flutter
   * CloudFunctionsService.submitSupportRequest at
   * lib/services/cloud_functions_service.dart:806-831).
   *
   * NOTE: This endpoint is `functions.https.onRequest(...)` (plain HTTP),
   * NOT a Firebase callable — it intentionally bypasses the `callFunction`
   * helper above, which uses `httpsCallable(...)`. Transport is the
   * browser's built-in `fetch` (no HTTP-client library in this project).
   *
   * The deployed region is `europe-west1` per
   * `cloud_functions/functions/src/index.ts:12`. The Flutter source
   * hardcodes `us-central1` (line 813) which is a bug — the function is
   * not deployed there. Both clients are now aligned on the correct
   * `europe-west1` URL.
   *
   * Matches Flutter semantics exactly: the response body is parsed
   * regardless of HTTP status (the cloud function returns the same
   * `{ success }` shape on both 200 and 500 — see
   * `cloud_functions/functions/src/functions/support/submit_support_request.ts:60-63`).
   * Any thrown error is swallowed and `false` is returned.
   */
  async submitSupportRequest(content: string, emailAddress: string): Promise<boolean> {
    try {
      const response = await fetch(
        'https://europe-west1-expause-com.cloudfunctions.net/submitSupportRequest',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
          },
          body: JSON.stringify({ content, emailAddress }),
        },
      );
      const result = await response.json();
      return Boolean(result?.success);
    } catch (error) {
      appLogger.error('Error submitSupportRequest', 'CloudFunctionsService.submitSupportRequest', { error: error as Error });
      return false;
    }
  }

  /**
   * Fetch the Revolut exchange rate for a currency pair (matching Flutter
   * CloudFunctionsService.getRevolutExchangeRate). Returns null on failure
   * or when Revolut auth/lookup fails server-side.
   */
  async getRevolutExchangeRate(params: {
    to: string;
    from: string;
    environment: string;
  }): Promise<ExchangeRateDto | null> {
    const { to, from, environment } = params;
    try {
      const response = await this.callFunction<{ data: { rate: string | null } }>(
        'getRevolutExchangeRate',
        { environment, from, to },
      );
      if (response.data?.rate == null) {
        return null;
      }
      return JSON.parse(response.data.rate) as ExchangeRateDto;
    } catch (error) {
      appLogger.error('Error getRevolutExchangeRate', 'CloudFunctionsService.getRevolutExchangeRate', { error: error as Error });
      return null;
    }
  }

  /**
   * Generate an Agora live-stream token for a video call (matching Flutter
   * CloudFunctionsService.generateLiveStreamingToken at
   * lib/services/cloud_functions_service.dart:69-96).
   *
   * Callable name `generateLiveStreamToken` (cloud_functions_service.dart:76);
   * payload keys are exactly `channel` / `uid` / `role`
   * (cloud_functions_service.dart:79-83).
   *
   * `HttpsCallableResult.data` equals Flutter's `response.data`, so Flutter's
   * `response.data['data']['appId']` / `['token']`
   * (cloud_functions_service.dart:90-91) maps to web `response.data.appId` /
   * `response.data.token` — i.e. the callable returns `{ data: { appId, token } }`.
   *
   * Returns the inner `data` object as a `JsonObject` (keeping the data-layer
   * surface JSON-only per data-layer.md); the domain service remaps it to a
   * `LiveStreamAuthDto` via `LiveStreamAuthRemapper`. Returns `null` on a null
   * response or a thrown error (parity with cloud_functions_service.dart:85-86,
   * 92-95).
   */
  async generateLiveStreamToken(
    channel: string,
    uid: number,
    role: string,
  ): Promise<JsonObject | null> {
    try {
      const response = await this.callFunction<{ data: JsonObject } | null>(
        'generateLiveStreamToken',
        { channel, uid, role },
      );
      return response?.data ?? null;
    } catch (error) {
      appLogger.error('Error generateLiveStreamToken', 'CloudFunctionsService.generateLiveStreamToken', { error: error as Error });
      return null;
    }
  }
}

// Export singleton instance
export const cloudFunctionsService = CloudFunctionsService.getInstance();